import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/api/client'
import { formatApiError } from '@/utils/httpErrors'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { ReportAcknowledgmentModal } from '@/components/ReportAcknowledgmentModal'
import type { Deal, Finding } from '@/types'

// ── Types ──────────────────────────────────────────────────────────────────────

/** QOE-CPA-FIXPACK-1 D1 — the pre-flight must show the deal's OWN procedure
 *  subset, not the 74-row catalogue. GET /qoe/deals/{id}/findings already
 *  resolves service level -> subset (the same mapping Scope & Tiers and the
 *  Quote page use, including any per-deal negotiated delta) and marks every row
 *  with in_scope. Those fields were simply dropped at this type boundary, so a
 *  Standard deal advertised "74 findings" and counted 20 procedures that were
 *  never bought. Nothing on the server needed to change.
 */
type PreflightFinding = Finding & {
  number?: number
  in_scope?: boolean
  state?: 'assessed' | 'not_in_scope' | 'data_missing' | 'unscored'
}

interface FindingsResponse {
  findings: PreflightFinding[]
  count: number
  /** Size of the whole catalogue — a constant, never this deal's scope. */
  catalogue_count?: number
  /** 'lite' | 'focused' | 'full' */
  scope_tier?: string
  /** How many procedures this engagement actually bought: 17 / 54 / 74. */
  in_scope_count?: number
  out_of_scope_count?: number
}

const SCOPE_TIER_LABELS: Record<string, string> = {
  lite: 'Essentials',
  focused: 'Standard',
  full: 'Comprehensive',
}

// REPORTGATE-WIRE-1: only the sign-off fields of the normalization envelope are
// read here — the Report Generator has no business with the figures themselves.
interface NormalizationGateResponse {
  status?: string | null
  approved_by?: string | null
  approved_at?: string | null
}

interface GenerateResponse {
  deal_id: string
  filename: string
  blob_url: string | null
  generated_at: string
  source: 'blob' | 'inline'
}

// ── Constants ──────────────────────────────────────────────────────────────────

const GENERATION_STAGES = [
  'Initializing report engine…',
  'Compiling financial data…',
  'Running normalization checks…',
  // Was "Generating AI narrative…" — no narrative generator exists (ENG-AI-1).
  // A progress caption that names a step the engine does not perform is a lie
  // the user has no way to check.
  'Scoring pre-flight findings…',
  'Assembling QoE document…',
  'Finalizing report…',
]

const SCORE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  PASS:    { bg: 'bg-green-100',  text: 'text-green-800',  label: 'Pass'    },
  REVIEW:  { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Review'  },
  FLAG:    { bg: 'bg-red-100',    text: 'text-red-800',    label: 'Flag'    },
  PENDING: { bg: 'bg-gray-100',   text: 'text-gray-600',   label: 'Pending' },
}

const REPORT_TIMEOUT = 120_000

// ── Helpers ────────────────────────────────────────────────────────────────────

function ScorePill({ score }: { score: string }) {
  const s = SCORE_STYLES[score] ?? SCORE_STYLES.PENDING
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  )
}

function CountBadge({ label, count, score }: { label: string; count: number; score: string }) {
  const s = SCORE_STYLES[score] ?? SCORE_STYLES.PENDING
  return (
    <div className={`flex flex-col items-center rounded-lg px-4 py-3 ${s.bg}`}>
      <span className={`text-2xl font-bold ${s.text}`}>{count}</span>
      <span className={`text-xs font-medium ${s.text}`}>{label}</span>
    </div>
  )
}

// ── Component ──────────────────────────────────────────────────────────────────

export function ReportGenerator() {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const paramDealId = searchParams.get('deal_id') ?? ''

  // Deal selector state
  const [deals, setDeals] = useState<Deal[]>([])
  const [dealsLoading, setDealsLoading] = useState(true)
  const [selectedDealId, setSelectedDealId] = useState(paramDealId)

  // Findings state. D1: `findings` holds ONLY the in-scope procedures — the
  // out-of-scope rows are dropped on arrival so no count, list or caption
  // downstream can accidentally re-introduce them.
  const [findings, setFindings] = useState<PreflightFinding[] | null>(null)
  const [scopeTier, setScopeTier] = useState<string | null>(null)
  const [outOfScopeCount, setOutOfScopeCount] = useState(0)
  const [findingsLoading, setFindingsLoading] = useState(false)
  const [findingsError, setFindingsError] = useState<string | null>(null)

  // Report gate state (REPORTGATE-WIRE-1). `null` means "not loaded yet" and is
  // deliberately distinct from "draft" — the button stays disabled either way,
  // but only "draft" earns the "go and get it signed off" caption.
  const [normGate, setNormGate] = useState<NormalizationGateResponse | null>(null)
  const [gateLoading, setGateLoading] = useState(false)

  // Generation state
  const [generating, setGenerating] = useState(false)
  const [stageIndex, setStageIndex] = useState(0)
  const [progress, setProgress] = useState(0)
  const [reportReady, setReportReady] = useState(false)
  const [reportFilename, setReportFilename] = useState<string | null>(null)
  const [reportBlobUrl, setReportBlobUrl] = useState<string | null>(null)
  const stageTimer = useRef<ReturnType<typeof setInterval> | null>(null)

  // Engagement letter state
  const [letterGenerating, setLetterGenerating] = useState(false)

  // Acknowledgment gate (REPORTGATE-1). The gate fires at the moment a document is
  // generated — never on page mount, when no deal is selected yet and the
  // acknowledgment would post an empty engagement_id.
  const [gateOpen, setGateOpen] = useState(false)
  const [acknowledgedDeals, setAcknowledgedDeals] = useState<string[]>([])
  const pendingAction = useRef<(() => void) | null>(null)

  // ── Load deals ──────────────────────────────────────────────────────────────

  useEffect(() => {
    setDealsLoading(true)
    apiClient.get<{ deals: Deal[] }>('/qoe/deals')
      .then(res => {
        setDeals(res.data.deals ?? [])
        // If URL param deal_id not already set and there's exactly one deal, auto-select
        if (!paramDealId && res.data.deals?.length === 1) {
          setSelectedDealId(res.data.deals[0].deal_id)
        }
      })
      .catch(err => toast.error(formatApiError(err)))
      .finally(() => setDealsLoading(false))
  }, [paramDealId])

  // ── Load findings when deal changes ─────────────────────────────────────────

  useEffect(() => {
    if (!selectedDealId) {
      setFindings(null)
      return
    }
    setFindingsLoading(true)
    setFindingsError(null)
    setFindings(null)
    setReportReady(false)
    apiClient.get<FindingsResponse>(`/qoe/deals/${selectedDealId}/findings`)
      .then(res => {
        const all = res.data.findings ?? []
        // D1: keep only what this engagement bought. `in_scope` absent means an
        // older backend that does not resolve scope — degrade to showing
        // everything rather than to showing nothing.
        const inScope = all.filter(f => f.in_scope !== false)
        setFindings(inScope)
        setScopeTier(res.data.scope_tier ?? null)
        setOutOfScopeCount(res.data.out_of_scope_count ?? all.length - inScope.length)
      })
      .catch(err => setFindingsError(formatApiError(err)))
      .finally(() => setFindingsLoading(false))
  }, [selectedDealId])

  // ── Load the report gate when deal changes (REPORTGATE-WIRE-1) ──────────────
  //
  // Separate from the findings fetch on purpose: findings are advisory (FLAG
  // rows are surfaced but never block), while THIS is the gate. A failure to
  // read it leaves the gate shut rather than open — an unreachable API must not
  // be a route to generating an unapproved report.

  useEffect(() => {
    if (!selectedDealId) {
      setNormGate(null)
      return
    }
    setGateLoading(true)
    setNormGate(null)
    apiClient.get<NormalizationGateResponse>(`/qoe/deals/${selectedDealId}/normalization`)
      .then(res => setNormGate({
        status: res.data.status ?? 'draft',
        approved_by: res.data.approved_by ?? null,
        approved_at: res.data.approved_at ?? null,
      }))
      .catch(() => setNormGate({ status: 'draft', approved_by: null, approved_at: null }))
      .finally(() => setGateLoading(false))
  }, [selectedDealId])

  // ── Carry forward an acknowledgment already recorded today ──────────────────

  useEffect(() => {
    if (!selectedDealId) return
    apiClient
      .get<{ acknowledged: boolean }>(`/v1/acknowledgments/report/${selectedDealId}`)
      .then(res => {
        if (res.data.acknowledged) {
          setAcknowledgedDeals(prev => (prev.includes(selectedDealId) ? prev : [...prev, selectedDealId]))
        }
      })
      .catch(() => {
        // Non-fatal: if the lookup fails the gate simply shows again.
      })
  }, [selectedDealId])

  // ── Stage cycling during generation ─────────────────────────────────────────

  const startStaging = () => {
    setStageIndex(0)
    setProgress(0)
    stageTimer.current = setInterval(() => {
      setStageIndex(i => {
        const next = i + 1
        if (next < GENERATION_STAGES.length) {
          setProgress(Math.round((next / GENERATION_STAGES.length) * 90))
          return next
        }
        return i
      })
    }, 5_000)
  }

  const stopStaging = () => {
    if (stageTimer.current) {
      clearInterval(stageTimer.current)
      stageTimer.current = null
    }
  }

  // Cleanup on unmount
  useEffect(() => () => stopStaging(), [])

  // ── Acknowledgment gate ──────────────────────────────────────────────────────

  /**
   * Run `action` only once the professional acknowledgment is on record for the
   * selected deal. Nothing is generated until the gate is satisfied, so Cancel
   * cannot be used to slip past it.
   */
  const withAcknowledgment = (action: () => void) => {
    if (!selectedDealId) {
      toast.error('Select an engagement first.')
      return
    }
    if (acknowledgedDeals.includes(selectedDealId)) {
      action()
      return
    }
    pendingAction.current = action
    setGateOpen(true)
  }

  const handleAcknowledged = () => {
    setGateOpen(false)
    setAcknowledgedDeals(prev => (prev.includes(selectedDealId) ? prev : [...prev, selectedDealId]))
    const action = pendingAction.current
    pendingAction.current = null
    action?.()
  }

  const handleGateCancel = () => {
    setGateOpen(false)
    pendingAction.current = null
    toast.info('Acknowledgment is required before a report or engagement letter can be produced.')
  }

  // ── Generate report ──────────────────────────────────────────────────────────

  const runGenerate = async () => {
    if (!selectedDealId) return
    setGenerating(true)
    setReportReady(false)
    startStaging()
    try {
      const res = await apiClient.post<GenerateResponse>(
        `/qoe/deals/${selectedDealId}/generate-report`,
        {},
        { timeout: REPORT_TIMEOUT },
      )
      stopStaging()
      setProgress(100)
      setReportFilename(res.data.filename)
      setReportBlobUrl(res.data.blob_url)
      setReportReady(true)
      toast.success('QoE report generated successfully')
    } catch (err: unknown) {
      stopStaging()
      setProgress(0)
      toast.error(formatApiError(err))
    } finally {
      setGenerating(false)
    }
  }

  // ── Download report ──────────────────────────────────────────────────────────

  const handleDownload = async () => {
    if (!selectedDealId) return
    try {
      const res = await apiClient.get(`/qoe/deals/${selectedDealId}/report`, {
        params: { stream: true },
        responseType: 'blob',
        timeout: REPORT_TIMEOUT,
      })
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = reportFilename ?? `QoE_Report_${selectedDealId}.docx`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Report downloaded')
    } catch (err) {
      toast.error(formatApiError(err))
    }
  }

  // ── Download PDF (client-facing deliverable) ─────────────────────────────────

  /**
   * QOE-TRUTH-AND-JOURNEY-1 R4/R5: the PDF is the client deliverable; the
   * .docx button above stays the CPA's internal working copy. This hits a
   * separate endpoint (not a query param on the docx one) because it carries
   * its own gate — the server 409s here if the report would still
   * self-declare DRAFT, and that 409 is expected, not a failure to swallow
   * with the generic error toast.
   */
  const handleDownloadPdf = async () => {
    if (!selectedDealId) return
    try {
      const res = await apiClient.get(`/qoe/deals/${selectedDealId}/report/pdf`, {
        responseType: 'blob',
        timeout: REPORT_TIMEOUT,
      })
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = (reportFilename ?? `QoE_Report_${selectedDealId}.docx`).replace(/\.docx$/i, '.pdf')
      a.click()
      URL.revokeObjectURL(url)
      toast.success('PDF downloaded')
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: unknown } }
      if (e?.response?.status === 409) {
        toast.error(
          'Report is still DRAFT — not all in-scope findings are concluded. ' +
            'The PDF is issued only once the report is FINAL; the .docx working copy is still available.',
        )
      } else {
        toast.error(formatApiError(err))
      }
    }
  }

  // ── Engagement letter ────────────────────────────────────────────────────────

  const runEngagementLetter = async () => {
    if (!selectedDealId) return
    setLetterGenerating(true)
    try {
      const res = await apiClient.post(
        `/qoe/deals/${selectedDealId}/engagement-letter`,
        {},
        { responseType: 'blob', timeout: REPORT_TIMEOUT },
      )
      const url = URL.createObjectURL(res.data as Blob)
      const deal = deals.find(d => d.deal_id === selectedDealId)
      const safeName = (deal?.business_name ?? selectedDealId).replace(/\s+/g, '_')
      const a = document.createElement('a')
      a.href = url
      a.download = `${safeName}_engagement_letter.docx`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Engagement letter downloaded')
    } catch (err: unknown) {
      const e = err as { response?: { status?: number } }
      if (e?.response?.status === 403) {
        toast.error('Engagement letters require CPA or super_admin role.')
      } else {
        toast.error(formatApiError(err))
      }
    } finally {
      setLetterGenerating(false)
    }
  }

  // ── Derived counts ───────────────────────────────────────────────────────────

  const counts = {
    PASS:    findings?.filter(f => f.score === 'PASS').length    ?? 0,
    REVIEW:  findings?.filter(f => f.score === 'REVIEW').length  ?? 0,
    FLAG:    findings?.filter(f => f.score === 'FLAG').length    ?? 0,
    PENDING: findings?.filter(f => f.score === 'PENDING').length ?? 0,
  }

  const pendingFindings = findings?.filter(f => f.score === 'PENDING') ?? []
  const flagFindings    = findings?.filter(f => f.score === 'FLAG')    ?? []
  const selectedDeal    = deals.find(d => d.deal_id === selectedDealId)

  // REPORTGATE-WIRE-1: the gate is the CPA's sign-off on the normalized
  // statement — not the findings.
  //
  // It used to be `pendingFindings.length === 0`. GET /findings enumerates the
  // whole 74-row catalogue and returns PENDING for every row that has no score,
  // and the only writer of scores is a one-at-a-time CPA PATCH that nothing in
  // the EBITDA Workspace calls — so the condition could never be satisfied and
  // the button was permanently disabled. Findings are now advisory only: they
  // are shown, and FLAGs are called out, but they do not block.
  const normApproved = normGate?.status === 'approved'
  // AUTHZ-PRODUCT-ROUTES-1: POST generate-report and GET report/pdf are
  // qoe:manage server-side (CPA/super_admin release authority) — an analyst
  // could previously click this and only discover the refusal from a 403.
  // Same role-term pattern as Normalization.tsx's canApprove.
  const isCpaOrAdmin = user?.role === 'cpa' || user?.role === 'super_admin'
  const canGenerate  = !!selectedDealId && !generating && !gateLoading && normApproved && isCpaOrAdmin

  const workspaceHref = selectedDealId
    ? `/normalization?deal_id=${encodeURIComponent(selectedDealId)}`
    : '/normalization'

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <ReportAcknowledgmentModal
        open={gateOpen}
        engagementId={selectedDealId}
        onAcknowledged={handleAcknowledged}
        onCancel={handleGateCancel}
      />
      <div className="max-w-3xl mx-auto py-8 px-4 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold" style={{ color: 'var(--color-primary)' }}>
          QoE Report Generator
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Review pre-flight findings, then generate the QoE report package.
        </p>
      </div>

      {/* ── Deal selector ─────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Select Engagement</CardTitle>
          <CardDescription>Choose the deal to generate a report for.</CardDescription>
        </CardHeader>
        <CardContent>
          {dealsLoading ? (
            <div className="h-9 bg-gray-100 animate-pulse rounded" />
          ) : deals.length === 0 ? (
            <p className="text-sm text-gray-500">No deals found. Create one from the Dashboard.</p>
          ) : (
            <Select value={selectedDealId} onValueChange={v => { if (v) setSelectedDealId(v) }}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a deal…" />
              </SelectTrigger>
              <SelectContent>
                {deals.map(d => (
                  <SelectItem key={d.deal_id} value={d.deal_id}>
                    {d.business_name || d.deal_id}
                    <span className="ml-2 text-xs text-gray-400">{d.status}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </CardContent>
      </Card>

      {/* ── Pre-flight findings ────────────────────────────────────────────── */}
      {selectedDealId && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Pre-Flight Checklist</CardTitle>
            {/* D1: the deal's own scope, phrased the way the Findings tab and
                the Quote page already phrase it — "54 procedures in scope",
                never the catalogue's 74. */}
            <CardDescription>
              {selectedDeal?.business_name ?? selectedDealId}
              {' · '}
              {findings ? `${findings.length} procedures in scope` : '– procedures'}
              {scopeTier && SCOPE_TIER_LABELS[scopeTier]
                ? ` · ${SCOPE_TIER_LABELS[scopeTier]}`
                : ''}
              {outOfScopeCount > 0
                ? ` · ${outOfScopeCount} not in this engagement`
                : ''}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {findingsLoading && (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-5 bg-gray-100 animate-pulse rounded" />
                ))}
              </div>
            )}

            {findingsError && (
              <Alert variant="destructive">
                <AlertDescription>{findingsError}</AlertDescription>
              </Alert>
            )}

            {findings && !findingsLoading && (
              <>
                {/* Score count grid */}
                <div className="grid grid-cols-4 gap-3">
                  <CountBadge label="Pass"    count={counts.PASS}    score="PASS"    />
                  <CountBadge label="Review"  count={counts.REVIEW}  score="REVIEW"  />
                  <CountBadge label="Flag"    count={counts.FLAG}    score="FLAG"    />
                  <CountBadge label="Pending" count={counts.PENDING} score="PENDING" />
                </div>

                {/* PENDING findings — informational, NOT blocking (see canGenerate) */}
                {pendingFindings.length > 0 && (
                  <Alert className="border-gray-300 bg-gray-50">
                    <AlertDescription className="text-gray-700 text-sm">
                      <p className="font-medium mb-2">
                        {pendingFindings.length} finding{pendingFindings.length > 1 ? 's' : ''} not yet scored. These are unscored checks, not blockers — they appear in the report as-is.
                      </p>
                      <ul className="list-disc pl-4 space-y-0.5 text-xs">
                        {pendingFindings.slice(0, 5).map(f => (
                          <li key={f.finding_id}>{f.label}</li>
                        ))}
                        {pendingFindings.length > 5 && (
                          <li className="text-gray-400">…and {pendingFindings.length - 5} more</li>
                        )}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Warning: FLAG findings (non-blocking) */}
                {flagFindings.length > 0 && (
                  <Alert className="border-amber-300 bg-amber-50">
                    <AlertDescription className="text-amber-800 text-sm">
                      <span className="font-medium">{flagFindings.length} flagged finding{flagFindings.length > 1 ? 's' : ''}</span>{' '}
                      will appear in the report. Review before delivery.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Finding list */}
                <div className="border rounded-md divide-y max-h-64 overflow-y-auto">
                  {findings.map(f => (
                    <div key={f.finding_id} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-gray-700 truncate mr-2">{f.label}</span>
                      <ScorePill score={f.score} />
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Generation panel ───────────────────────────────────────────────── */}
      {selectedDealId && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Generate Report</CardTitle>
            <CardDescription>
              Renders the full QoE package including financial analysis and AI narrative.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {generating && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{GENERATION_STAGES[stageIndex]}</span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} />
              </div>
            )}

            {reportReady && (
              <Alert className="border-green-300 bg-green-50">
                <AlertDescription className="text-green-800 text-sm font-medium">
                  Report generated successfully.{' '}
                  {reportBlobUrl ? 'Saved to Azure Blob Storage.' : 'Inline bytes ready for download.'}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap gap-3">
              <Button
                style={{ backgroundColor: 'var(--color-primary)' }}
                disabled={!canGenerate}
                data-testid="generate-report"
                onClick={() => withAcknowledgment(runGenerate)}
              >
                {generating ? 'Generating…' : reportReady ? 'Regenerate' : 'Generate Report'}
              </Button>

              {reportReady && (
                <Button variant="outline" onClick={handleDownload}>
                  Download .docx
                </Button>
              )}

              {/* QOE-TRUTH-AND-JOURNEY-1 R4/R5: PDF is the client-facing
                  deliverable; docx above is the CPA's internal working copy.
                  Rendered once a report exists — the server, not this
                  button, is the completeness gate (409 while DRAFT).
                  AUTHZ-PRODUCT-ROUTES-1: report/pdf is qoe:manage
                  server-side (same release authority as Generate Report
                  above), so also gated on isCpaOrAdmin — an analyst can
                  still see and download the .docx working copy. */}
              {reportReady && isCpaOrAdmin && (
                <Button variant="outline" onClick={handleDownloadPdf} data-testid="download-pdf">
                  Download PDF
                </Button>
              )}

              <Button
                variant="outline"
                disabled={!selectedDealId || letterGenerating}
                data-testid="engagement-letter"
                onClick={() => withAcknowledgment(runEngagementLetter)}
              >
                {letterGenerating ? 'Generating…' : 'Engagement Letter'}
              </Button>
            </div>

            {/* REPORTGATE-WIRE-1: the old caption named a place with no link and
                no working control, so it was a dead end. It now links straight
                to the workspace with the deal pre-selected. */}
            {!generating && !gateLoading && !normApproved && (
              <Alert className="border-amber-300 bg-amber-50" data-testid="gate-blocked">
                <AlertDescription className="text-amber-800 text-sm">
                  <span className="font-medium">Awaiting CPA sign-off.</span>{' '}
                  The normalized statement has not been approved yet.{' '}
                  <Link
                    to={workspaceHref}
                    className="font-medium underline underline-offset-2"
                    data-testid="gate-workspace-link"
                  >
                    Open the EBITDA Workspace
                  </Link>{' '}
                  and use <span className="font-medium">Approve &amp; Complete Normalization</span>{' '}
                  (CPA or super admin) to enable generation.
                </AlertDescription>
              </Alert>
            )}

            {normApproved && !generating && (
              <p className="text-xs text-gray-500" data-testid="gate-approved">
                Normalization approved
                {normGate?.approved_by ? ` by ${normGate.approved_by}` : ''}
                {normGate?.approved_at
                  ? ` on ${new Date(normGate.approved_at).toLocaleString()}`
                  : ''}.
              </p>
            )}
          </CardContent>
        </Card>
      )}
      </div>
    </>
  )
}
